// Cache service for refreshing cache files
import { initSocket, sendControl } from './socket';
import { getApiUrl } from './apiConfig';

/**
 * Refresh all cache files (cache_data.json, question_cache.json, reading_log.json)
 * @returns {Promise<Object>} Refresh results
 */
export const refreshCache = async () => {
  try {
    console.log('🔄 Refreshing cache files...');
    
    const response = await fetch(getApiUrl('api/refresh-cache'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      console.log('✅ Cache refresh successful:', data);
      return {
        success: true,
        data: data
      };
    } else {
      console.error('❌ Cache refresh failed:', data.error);
      return {
        success: false,
        error: data.error
      };
    }

  } catch (error) {
    console.error('❌ Error refreshing cache:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Refresh cache and trigger backend pipeline restart
 * This ensures fresh data is available for quizzes
 * @returns {Promise<Object>} Refresh results
 */
export const refreshCacheAndRestart = async () => {
  try {
    console.log('🔄 Refreshing cache and restarting pipeline...');
    
    // First refresh the cache
    const refreshResult = await refreshCache();
    
    if (refreshResult.success) {
      // Then trigger backend pipeline restart
      console.log('🚀 Triggering backend pipeline restart...');
      sendControl("start"); // This will restart the OCR pipeline
      
      return {
        success: true,
        message: 'Cache refreshed and pipeline restarted',
        data: refreshResult.data
      };
    } else {
      return refreshResult;
    }

  } catch (error) {
    console.error('❌ Error refreshing cache and restarting:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check cache status without refreshing
 * @returns {Promise<Object>} Cache status
 */
export const getCacheStatus = async () => {
  try {
    console.log('📊 Checking cache status...');
    
    // Check latest quiz hashes as an indicator of cache health
    const response = await fetch(getApiUrl('api/latest-quiz-hashes'));
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      console.log('✅ Cache status check successful:', data);
      return {
        success: true,
        data: {
          hashes_available: data.total_available,
          latest_hashes: data.hashes,
          cache_healthy: data.total_available > 0
        }
      };
    } else {
      console.error('❌ Cache status check failed:', data.error);
      return {
        success: false,
        error: data.error
      };
    }

  } catch (error) {
    console.error('❌ Error checking cache status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
